import { useCallback, useEffect, useRef, useMemo, useImperativeHandle, useState } from 'react';
import ReactDOM from 'react-dom';
import { PdfPageContainer } from './PdfPageContainer';
import AddToChronologyDialog from './AddToChronology';
import { useViewportPageIndexContext, useReactVirtualizedListResize } from '../hooks';
import { List, ListRowProps, globalToastService } from '@repo/atomic-components';
import { debounce } from '@/utils';
import {
  BundleType,
  clearPageInfoCache,
  makeSelectBundlePageDataByPageIndex,
  makeSelectBundlePageIndexByPageId,
  removeChronologyEntriesFromBundlePages,
  removeChronologyEntriesFromPageInfoCache,
  selectBundleMetadataByStoreKey,
  selectBundlePageIds,
  selectBundlePagesData,
  selectBundlePageScale,
  selectEnabledStates,
  selectSelectedValues,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectSharedDataInitialPageId,
  selectViewportPageIdForRestorePageIndexInStore,
  selectViewportPageIndexInStore,
  store,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  BROADCAST_ADD_TO_CHRONOLOGY_AND_LIGHT_BUNDLE_REFRESH,
  BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH,
  BROADCAST_REDACT_PAGES_BUNDLE_REFRESH,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
} from '@/broadcast';
import {
  api,
  BUNDLE_TAGS,
  BUNDLE_SECTIONS_TAG,
  BUNDLE_DUPLICATES_TAG,
  BUNDLE_SIMILARS_TAG,
  getBundleSectionsApi,
  refreshBundleSectionsWithCurrentFilters,
  useLazyGetBundleQuery,
  useLazyGetBundleSectionsQuery,
} from '@/api';
import { calculatePageSizes } from '../utils';

const SHIFT_FOR_PAGE_NUMBER_HTML_BLOCK = 56;
const DEBOUNCE_SCROLL_DELAY_MS = 50;
const HIDDEN_PAGE_SCALE_DELAY_MS = 300;
const HIDDEN_SCROLLING_DELAY_MS = 500;
const HIDDEN_NEXT_ITERATION_DELAY_MS = 150;

export const ReactVirtualizedList = () => {
  const dispatch = useAppDispatch();
  const [pendingScrollToPageId, setPendingScrollToPageId] = useState<string | null>(null);
  const [showChronologyDialog, setShowChronologyDialog] = useState<boolean>(false);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [selectedTextForChronology, setSelectedTextForChronology] = useState<string>('');

  // Handler for adding new text and pageId
  const handleAddToChronology = useCallback(
    (newText: string, newPageId: string) => {
      // Add pageId only if not already present
      setSelectedPageIds((prev) => (prev.includes(newPageId) ? prev : [...prev, newPageId]));

      // Add text with line breaks
      setSelectedTextForChronology((prev) => (prev ? `${prev}\n\n${newText}` : newText));

      // Open dialog if not already open
      if (!showChronologyDialog) {
        setShowChronologyDialog(true);
      }
    },
    [showChronologyDialog],
  );

  // Handler for closing dialog and resetting state
  const handleCloseChronologyDialog = useCallback(() => {
    setShowChronologyDialog(false);
    setSelectedPageIds([]);
    setSelectedTextForChronology('');
  }, []);

  const documentReviewPageContext = useViewportPageIndexContext();
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);

  const bundlePageScale = useAppSelector(selectBundlePageScale);

  const hasInitializedScrollToIndex = useRef<boolean>(false);

  // The page a cross-tab "View in Full/Light Bundle" jump or a
  // `page_id_in_viewport_on_init` deep link asked us to land on.
  const sharedDataInitialPageId = useAppSelector(selectSharedDataInitialPageId);
  const hasLandedOnInitialPageRef = useRef(false);

  const viewportPageIdForRestorePageIndexInStoreRef = useRef<string | null>(null);
  const viewportPageIdForRestorePageIndexInStore = useAppSelector(selectViewportPageIdForRestorePageIndexInStore);

  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getBundlePageDataByPageIndex = useAppSelector(makeSelectBundlePageDataByPageIndex);
  /**
   * Watched only so we can bust react-virtualized's row-height cache when the
   * underlying Map reference changes — e.g. after the optimistic rotate update
   * swaps a page's width/height, or after /bundle refetches with canonical
   * dimensions. Without an explicit recompute, the List keeps the stale height
   * for that index and a rotated page overflows into the next row.
   */
  const bundlePagesData = useAppSelector(selectBundlePagesData);
  const [triggerGetBundle] = useLazyGetBundleQuery();
  const [triggerBundleSections] = useLazyGetBundleSectionsQuery();

  // Guards for the empty-state message: only show "no pages matching filters" once
  // the initial sections request has actually settled and the user has at least
  // one filter on. Without these, the message flashes on initial load and during
  // case switches when bundlePageIds is briefly empty.
  //
  // Read the request status from the shared RTK Query cache directly rather than
  // from a local useLazyQuery instance — the first fetch fires from
  // DocumentReviewPage, so a hook instance here would stay `uninitialized` forever.
  const enabledStates = useAppSelector(selectEnabledStates);
  const selectedValues = useAppSelector(selectSelectedValues);
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const hasActiveFilters = useMemo(() => {
    const anyEnabled = Object.values(enabledStates).some(Boolean);
    const anyValues = Object.values(selectedValues).some((v) => (Array.isArray(v) ? v.length > 0 : !!v));
    return anyEnabled || anyValues;
  }, [enabledStates, selectedValues]);
  const bundleSectionsQueryState = useAppSelector((state) =>
    bundleId ? getBundleSectionsApi.endpoints.getBundleSections.select({ bundleId })(state) : null,
  );
  const requestSettled =
    !!bundleSectionsQueryState && (bundleSectionsQueryState.isSuccess || bundleSectionsQueryState.isError);
  const listRef = useRef<List>(null);
  const pageInViewRef = useRef<[number, number]>([0, 0]);
  const [isHiddenScrolling, setIsHiddenScrolling] = useState<boolean>(false);
  const [hiddenScrollingPageIndex, setHiddenScrollingPageIndex] = useState<number | null>(null);
  const hiddenScrollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { containerRef, containerHeight, containerWidth, setSizesManually } = useReactVirtualizedListResize();

  const getRowHeight = useCallback(
    ({ index }: { index: number }) => {
      const rowData = getBundlePageDataByPageIndex(index);

      // Guard against invalid containerHeight to prevent NaN calculations
      // This can happen during bundle refetch when dimensions are temporarily unavailable
      if (!containerHeight || containerHeight <= 0) {
        return 800; // Return a reasonable fallback height
      }

      const { pageHeight } = calculatePageSizes({
        height: rowData?.pageDimensions.height,
        width: rowData?.pageDimensions.width,
        bundlePageScale: bundlePageScale,
        containerHeight,
      });

      // Additional guard: if pageHeight is NaN or invalid, return fallback
      if (!pageHeight || isNaN(pageHeight)) {
        return 800; // Return a reasonable fallback height
      }

      return pageHeight + SHIFT_FOR_PAGE_NUMBER_HTML_BLOCK;
    },

    [getBundlePageDataByPageIndex, bundlePageScale, containerHeight],
  );

  const debouncedHandleScroll = useMemo(
    () =>
      /**
       * This function updated viewportPageIndex in the middle of list view port
       */
      debounce(({ scrollHeight, scrollTop }: { scrollHeight: number; scrollTop: number }) => {
        const [startIndex, endIndex] = pageInViewRef.current;

        let closestIndex = 0;
        let closestDistance = Number.MAX_VALUE;

        for (let index = startIndex; index <= endIndex; index++) {
          const offsetCurrent = listRef.current?.getOffsetForRow({
            index: index,
          });
          const nextIndex = index + 1 <= bundlePageIds.length - 1 ? index + 1 : index;
          const offsetNext =
            index === bundlePageIds.length - 1 ? scrollHeight : listRef.current?.getOffsetForRow({ index: nextIndex });

          if (offsetCurrent === undefined || offsetNext === undefined) continue;

          const elementCenter = (offsetCurrent + offsetNext) / 2;
          const distanceToCenter = Math.abs(elementCenter - (scrollTop + containerHeight / 2));

          if (distanceToCenter < closestDistance) {
            closestDistance = distanceToCenter;
            closestIndex = index;
          }

          documentReviewPageContext.onChangeReactVirtualizedViewportPageIndex(closestIndex);
        }
      }, DEBOUNCE_SCROLL_DELAY_MS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [containerHeight, bundlePageIds, documentReviewPageContext.onChangeReactVirtualizedViewportPageIndex],
  );
  useEffect(() => {
    if (pendingScrollToPageId && bundlePageIds.length > 0) {
      const viewPortPageIndex = getBundlePageIndexByPageId(pendingScrollToPageId);
      if (viewPortPageIndex >= 0 && activeBundleType === BundleType.LIGHT) {
        documentReviewPageContext.reactVirtualizedRef.current?.scrollToRowWithDebounceByViewportPageIndex(
          viewPortPageIndex,
        );
      }
      setPendingScrollToPageId(null);
    }
  }, [bundlePageIds, pendingScrollToPageId, getBundlePageIndexByPageId, activeBundleType, documentReviewPageContext]);
  useEffect(() => {
    /**
     * Init list container sizes, because ResizeObserver didn't trigger update event.
     */
    if ((containerHeight === 0 || containerWidth === 0) && containerRef && containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setSizesManually(height, width);
    }

    /**
     * Broadcast listeners
     */
    const controller = new AbortController();
    const signal = controller.signal;

    const handleCheckIfAnyBundlePageExistAndScrollToRowWithPageIdEvent = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Ping Listener Aborted: ${BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE}.`);
        return;
      }

      const { payload, reply } = event.detail;
      console.info('Ping received:', payload);

      if (
        'showPageIdInViewport' in payload &&
        typeof payload.showPageIdInViewport === 'string' &&
        payload.showPageIdInViewport.length
      ) {
        const viewPortPageIndex = getBundlePageIndexByPageId(payload.showPageIdInViewport);
        if (viewPortPageIndex < 0) {
          // The requested page isn't in this bundle's current view (hidden by
          // the active filters), so there's nothing to scroll to. Let the user
          // know instead of silently doing nothing.
          globalToastService.show({
            severity: 'info',
            summary: 'Page hidden by filters',
            detail: 'This page is hidden by the filters applied to this bundle.',
          });
        } else {
          documentReviewPageContext.reactVirtualizedRef.current?.scrollToRowWithDebounceByViewportPageIndex(
            viewPortPageIndex,
          );
        }
      }

      // Send response
      reply({ pong: true });
    };

    const handleRefreshPageDataWhenDataAddedToChronology = () => {
      if (signal.aborted) {
        console.info(`Ping Listener Aborted: ${BROADCAST_ADD_TO_CHRONOLOGY_AND_LIGHT_BUNDLE_REFRESH}.`);
        return;
      }

      refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
      dispatch(api.util.invalidateTags([...BUNDLE_TAGS].filter((tag) => tag !== BUNDLE_SECTIONS_TAG)));
    };
    const updateData = async (targetBundleId: string) => {
      if (!targetBundleId) return;
      await triggerGetBundle({ bundleId: targetBundleId }).unwrap();
      await triggerBundleSections({
        bundleId: targetBundleId,
      }).unwrap();
    };

    // A chronology entry was deleted / undone / redone in the Chronology tab.
    // This runs in the Bundle tab's own store, so it's safe to touch bundle
    // state here (unlike the chronology API endpoints, which live in the
    // other tab).
    //
    // If the payload carries the removed entryIds we take a *targeted*
    // approach: strip those ids from bundlePagesData AND from the
    // pageInfoCache. Wiping the whole pageInfoCache would work too, but
    // that cache also stores bookmarkDate / clusterDate / dates / etc.
    // — anything the Add-to-Chronology dialog uses to prefill the date
    // field. Blowing it away made the dialog blank until the user
    // scrolled enough for the lazy prefetch to repopulate the cache.
    // Without entryIds (undo/redo case) we still have to clear the
    // whole cache because we don't know which entries changed.
    const handleChronologyChanged = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Ping Listener Aborted: ${BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH}.`);
        return;
      }

      const entryIds: unknown = event.detail?.payload?.entryIds;
      if (Array.isArray(entryIds) && entryIds.length) {
        const ids = entryIds as string[];
        dispatch(removeChronologyEntriesFromBundlePages({ entryIds: ids }));
        dispatch(removeChronologyEntriesFromPageInfoCache({ key: BundleType.FULL, entryIds: ids }));
        dispatch(removeChronologyEntriesFromPageInfoCache({ key: BundleType.LIGHT, entryIds: ids }));
      } else {
        // Undo/redo path — no entryIds available, safest to reconcile
        // via a full cache wipe + bundle refetch.
        dispatch(clearPageInfoCache({ key: BundleType.FULL }));
        dispatch(clearPageInfoCache({ key: BundleType.LIGHT }));
      }
      void updateData(bundleId ?? '');
    };

    // Pages were redacted in another Bundle tab. Refetch only /bundle for the
    // new page URLs, as the redacting tab does, so this tab keeps its scroll.
    const handlePagesRedacted = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Ping Listener Aborted: ${BROADCAST_REDACT_PAGES_BUNDLE_REFRESH}.`);
        return;
      }

      const redactedCaseId: unknown = event.detail?.payload?.caseId;
      const caseId = selectBundleMetadataByStoreKey()(store.getState())?.caseId;
      if (!bundleId || (redactedCaseId && caseId && redactedCaseId !== caseId)) return;

      triggerGetBundle({ bundleId })
        .unwrap()
        .catch((error) => console.error('Refreshing bundle after redaction in another tab failed:', error));
    };

    const handleRefreshPageDataAndScroll = async (event: CustomEvent) => {
      const { payload, reply } = event.detail;
      try {
        // Always refresh this tab's own bundle. The broadcast payload may carry an
        // empty/missing bundleId when FB's otherBundleId isn't populated yet, which
        // was causing bundle?bundle_id= 400s on the Light Bundle tab.
        await updateData(bundleId ?? '');
        // A page was just extracted into this Light Bundle from the Full Bundle,
        // so the duplicate/similar relationships have changed. Refetch them so the
        // tooltips update (e.g. the link flips from "Full Bundle" to this Light
        // Bundle page) without needing a full page reload.
        dispatch(api.util.invalidateTags([BUNDLE_DUPLICATES_TAG, BUNDLE_SIMILARS_TAG]));
        if (
          'showPageIdInViewport' in payload &&
          typeof payload.showPageIdInViewport === 'string' &&
          payload.showPageIdInViewport.length
        ) {
          setPendingScrollToPageId(payload.showPageIdInViewport);
        }
        // Send response
        reply({ pong: true });
      } catch (error) {
        console.error('Error in handleRefreshPageDataAndScroll:', error);
        reply({ pong: false, error: 'Failed to refresh data' });
      }
    };

    // Both Full and Light tabs render the "in chronology" indicator, so both
    // must react to chronology changes from the Chronology tab.
    window.addEventListener(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, handleChronologyChanged as EventListener, {
      signal,
    });
    window.addEventListener(BROADCAST_REDACT_PAGES_BUNDLE_REFRESH, handlePagesRedacted as EventListener, {
      signal,
    });

    if (activeBundleType === BundleType.FULL) {
      window.addEventListener(
        BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
        handleCheckIfAnyBundlePageExistAndScrollToRowWithPageIdEvent as EventListener,
        {
          signal,
        },
      );
    }

    if (activeBundleType === BundleType.LIGHT) {
      window.addEventListener(
        BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
        handleCheckIfAnyBundlePageExistAndScrollToRowWithPageIdEvent as EventListener,
        {
          signal,
        },
      );

      window.addEventListener(
        BROADCAST_ADD_TO_CHRONOLOGY_AND_LIGHT_BUNDLE_REFRESH,
        handleRefreshPageDataWhenDataAddedToChronology as EventListener,
        {
          signal,
        },
      );
      window.addEventListener(
        BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
        (event) => handleRefreshPageDataAndScroll(event as CustomEvent),
        {
          signal,
        },
      );
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBundleType, bundleId, getBundlePageIndexByPageId]);

  const scrollWithRecomputeRowHeights = useCallback((newPageIndex: number) => {
    setIsHiddenScrolling(true);
    setHiddenScrollingPageIndex(newPageIndex);
    listRef.current?.recomputeRowHeights();
    listRef.current?.scrollToRow(newPageIndex);
    hiddenScrollingTimeoutRef.current = setTimeout(() => {
      setIsHiddenScrolling(false);
      setHiddenScrollingPageIndex(null);
    }, HIDDEN_PAGE_SCALE_DELAY_MS);
  }, []);

  // Zoom no longer flips the list to visibility:hidden — PdfPageContainer's
  // CSS scale + snapshot overlay keeps each page looking correct through
  // the transition, and hiding the list would just expose the grey
  // background behind it. We still need to recompute row heights (rows are
  // taller/shorter after zoom) and re-anchor the scroll to the current page.
  //
  // Centre the anchor page in the viewport rather than aligning to its top:
  // at low zoom a short/landscape page can fit inside the viewport with
  // spare room below, so a top-aligned scroll leaves the viewport centre
  // sitting on the *next* row — debouncedHandleScroll then treats that as
  // the current page and every subsequent zoom drifts one page further.
  // The List's own scrollToAlignment prop is 'start' outside of scroll-hide,
  // so compute the centred offset manually and scrollToPosition instead.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.recomputeRowHeights();
    const offset = list.getOffsetForRow({ alignment: 'center', index: viewportPageIndexInStore });
    if (typeof offset === 'number') {
      list.scrollToPosition(offset);
    } else {
      list.scrollToRow(viewportPageIndexInStore);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundlePageScale]);

  // Recompute row heights when containerHeight changes from invalid to valid
  // This ensures proper sizing after bundle refetch completes
  useEffect(() => {
    if (containerHeight > 0 && listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, [containerHeight]);

  // Horizontally centre the (possibly wider-than-viewport) List over the
  // visible viewport. The List's own width is the widest zoomed page so its
  // rows aren't clipped, but that means it can stick out past the right edge
  // of the outer scroll container. We push scrollLeft to (list width -
  // viewport width) / 2 so the pages appear centred; the user can still
  // horizontal-scroll from there to see anything wider than the viewport.
  useEffect(() => {
    const outer = containerRef.current;
    if (!outer) return;
    const overflow = outer.scrollWidth - outer.clientWidth;
    outer.scrollLeft = overflow > 0 ? overflow / 2 : 0;
  }, [containerWidth, bundlePageScale]);

  /**
   * Recompute row heights whenever the bundle pages Map or the page order
   * changes. react-virtualized caches row heights by index, so an optimistic
   * width/height swap on rotation (or fresh dimensions from a /bundle
   * refetch) needs an explicit recompute, otherwise the row container keeps
   * its stale height and a rotated portrait page overflows into the next row.
   * A reorder invalidates the same cache: index -> page is no longer what it
   * was when the heights were measured.
   */
  useEffect(() => {
    if (listRef.current) {
      listRef.current.recomputeRowHeights();
    }
  }, [bundlePagesData, bundlePageIds]);

  useEffect(() => {
    if (
      typeof viewportPageIdForRestorePageIndexInStore === 'string' &&
      !!viewportPageIdForRestorePageIndexInStore.length &&
      viewportPageIdForRestorePageIndexInStoreRef.current === null
    ) {
      viewportPageIdForRestorePageIndexInStoreRef.current = viewportPageIdForRestorePageIndexInStore;
    }

    if (
      typeof viewportPageIdForRestorePageIndexInStoreRef.current === 'string' &&
      !!viewportPageIdForRestorePageIndexInStoreRef.current.length &&
      viewportPageIdForRestorePageIndexInStore === null
    ) {
      const newPageIndex = getBundlePageIndexByPageId(viewportPageIdForRestorePageIndexInStoreRef.current);
      if (typeof newPageIndex === 'number' && newPageIndex >= 0) {
        viewportPageIdForRestorePageIndexInStoreRef.current = null;
        scrollWithRecomputeRowHeights(newPageIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewportPageIdForRestorePageIndexInStore]);

  /**
   * Land on the page the cross-tab jump / deep link asked for.
   *
   * Target-driven rather than one-shot, and deliberately not left to the List's
   * `scrollToIndex` prop: on a cold load the list mounts and paints — retiring that
   * prop — well before the queries that resolve the page's index have settled, so
   * the prop is already `undefined` by the time the target arrives and the viewer
   * stays on page 1 while the thumbnails rail sits on the requested page. This
   * re-runs as the pages and the container size arrive and scrolls as soon as the
   * page is actually resolvable.
   */
  useEffect(() => {
    if (!sharedDataInitialPageId || hasLandedOnInitialPageRef.current) return;
    // Nothing to scroll yet: the List only mounts once the container has a
    // measured height and the bundle has pages.
    if (containerHeight <= 0 || !listRef.current) return;

    const newPageIndex = getBundlePageIndexByPageId(sharedDataInitialPageId);
    // Not in this bundle's current view — hidden by the active filters, or its
    // section hasn't loaded yet. Keep waiting; DocumentReviewPage retires the
    // target if it never turns up.
    if (newPageIndex < 0) return;

    hasLandedOnInitialPageRef.current = true;

    // Already the top row (the initial scrollToIndex prop got there first), so don't
    // flash the hidden-scroll transition for nothing. Deliberately not "anywhere in
    // the rendered range": that range includes a row only just peeking in at the
    // bottom, which is not landed.
    const [startIndex] = pageInViewRef.current;
    if (newPageIndex === startIndex) return;

    scrollWithRecomputeRowHeights(newPageIndex);
  }, [
    sharedDataInitialPageId,
    containerHeight,
    bundlePageIds,
    getBundlePageIndexByPageId,
    scrollWithRecomputeRowHeights,
  ]);

  useImperativeHandle(documentReviewPageContext.reactVirtualizedRef, () => {
    return {
      scrollToRowWithDebounceByViewportPageIndex: (newViewportPageIndex: number) => {
        /**
         * Update only if page index changed
         */
        if (viewportPageIndexInStore !== newViewportPageIndex) {
          /**
           * If the diff only in 1 page (up or down) than just scroll
           */
          if (Math.abs(viewportPageIndexInStore - newViewportPageIndex) === 1) {
            listRef.current?.scrollToRow(newViewportPageIndex);
          } else {
            setIsHiddenScrolling(true);
            setHiddenScrollingPageIndex(newViewportPageIndex);
            listRef.current?.scrollToRow(newViewportPageIndex);
            hiddenScrollingTimeoutRef.current = setTimeout(() => {
              setIsHiddenScrolling(false);
              setHiddenScrollingPageIndex(null);
            }, HIDDEN_SCROLLING_DELAY_MS);
          }
        }
      },
      openChronologyForCurrentPage: () => {
        // If the user has an active text selection on a rendered PDF page, use
        // that — same as the right-click "Add to Chronology" flow. Each page's
        // row wrapper carries `data-testid={pageId}`, so we walk up from the
        // selection's common ancestor to find which page it belongs to. If
        // there's no selection, fall back to the current viewport page with
        // empty text.
        const selection = window.getSelection();
        const selectedText = selection?.toString() ?? '';
        let selectedPageId: string | null = null;
        if (selection && selection.rangeCount > 0 && selectedText.trim()) {
          const range = selection.getRangeAt(0);
          const node = range.commonAncestorContainer;
          const startElement = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
          const pageWrapper = startElement?.closest('[data-testid]') as HTMLElement | null;
          const candidateId = pageWrapper?.dataset.testid;
          if (candidateId && bundlePageIds.includes(candidateId)) {
            selectedPageId = candidateId;
          }
        }
        const pageId = selectedPageId ?? bundlePageIds[viewportPageIndexInStore];
        if (!pageId) return;
        const cleanedText = selectedText.replace(/[\r\n]+/g, ' ').trim();
        handleAddToChronology(cleanedText, pageId);
      },
    };
  });

  // Memoised so react-virtualized's internal Grid (a PureComponent) can skip
  // re-rendering visible rows when unrelated state changes in this component.
  const rowRenderer = useCallback(
    ({ key, ...rowProps }: ListRowProps) => {
      const rowData = getBundlePageDataByPageIndex(rowProps.index);

      if (!rowData || !rowData.pageUri || !rowData.pageDimensions.width) {
        return <span>File data is in an incorrect format</span>;
      }

      // Guard against invalid containerHeight during refetch
      if (!containerHeight || containerHeight <= 0) {
        return (
          <div data-testid={rowData.pageId} key={key} style={rowProps.style}>
            <div style={{ height: '800px' }} className="align-items-center flex w-full justify-center">
              {/* Fallback placeholder during dimension calculation */}
            </div>
            <div className="align-items-center mb-4 flex justify-center py-2 text-[12px]">{rowProps.index + 1}</div>
          </div>
        );
      }

      const { pageHeight, pageWidth } = calculatePageSizes({
        height: rowData.pageDimensions.height,
        width: rowData.pageDimensions.width,
        bundlePageScale: bundlePageScale,
        containerHeight,
      });

      // Additional guard: if calculations produce NaN, use fallback
      const safePageHeight = !pageHeight || isNaN(pageHeight) ? 800 : pageHeight;
      const safePageWidth = !pageWidth || isNaN(pageWidth) ? 600 : pageWidth;

      return (
        <div data-testid={rowData.pageId} key={key} style={rowProps.style}>
          <div style={{ height: `${safePageHeight}px` }} className="align-items-center flex w-full justify-center">
            {/* Don't download the PDFs while a long scroll-jump is hiding
                the list — the destination pages are off-screen anyway. */}
            {!isHiddenScrolling && (
              <div className="border-fletchers-gray-110 border">
                <PdfPageContainer
                  item={rowData}
                  pageHeight={safePageHeight}
                  pageWidth={safePageWidth}
                  onOpenChronologyDialog={handleAddToChronology}
                />
              </div>
            )}
          </div>
          <div className="align-items-center mb-4 flex justify-center py-2 text-[12px]">{rowProps.index + 1}</div>
        </div>
      );
    },
    [getBundlePageDataByPageIndex, containerHeight, bundlePageScale, isHiddenScrolling, handleAddToChronology],
  );

  const handleRowsRendered = ({ startIndex, stopIndex }: { startIndex: number; stopIndex: number }) => {
    pageInViewRef.current = [startIndex, stopIndex];

    hasInitializedScrollToIndex.current = true;

    if (isHiddenScrolling && typeof hiddenScrollingPageIndex === 'number') {
      if (hiddenScrollingPageIndex >= startIndex && hiddenScrollingPageIndex <= stopIndex) {
        if (hiddenScrollingPageIndex !== viewportPageIndexInStore) {
          documentReviewPageContext.onChangeReactVirtualizedViewportPageIndex(hiddenScrollingPageIndex);
        }
      } else {
        listRef.current?.scrollToRow(hiddenScrollingPageIndex);
      }

      /**
       * Sometimes this function does not call after zoom and list stay hidden, to prevent this we manually disabled it in 500ms after last call
       */
      if (hiddenScrollingTimeoutRef.current) {
        clearTimeout(hiddenScrollingTimeoutRef.current);
      }
      hiddenScrollingTimeoutRef.current = setTimeout(() => {
        setIsHiddenScrolling(false);
        setHiddenScrollingPageIndex(null);
      }, HIDDEN_NEXT_ITERATION_DELAY_MS);
    }
  };

  const handleScroll = useCallback(
    (params: { scrollHeight: number; scrollTop: number }) => {
      if (!isHiddenScrolling) {
        debouncedHandleScroll(params);
      }
    },
    [debouncedHandleScroll, isHiddenScrolling],
  );

  return (
    <>
      <div ref={containerRef} className="bg-fletchers-gray-50 h-full w-full overflow-auto">
        {bundlePageIds.length === 0 && hasActiveFilters && requestSettled && (
          <div className="flex h-full items-center justify-center">
            <span className="text-fletchers-gray-175 text-[14px]">No pages matching filters</span>
          </div>
        )}
        {bundlePageIds.length > 0 && !!containerHeight && !!containerWidth && (
          <List
            ref={listRef}
            width={containerWidth} // List width
            height={containerHeight} // List height
            rowCount={bundlePageIds.length}
            rowHeight={getRowHeight} // Item height
            rowRenderer={rowRenderer}
            onRowsRendered={handleRowsRendered}
            scrollToAlignment={isHiddenScrolling ? 'center' : 'start'}
            onScroll={handleScroll}
            style={isHiddenScrolling ? { visibility: 'hidden' } : undefined}
            scrollToIndex={hasInitializedScrollToIndex.current ? undefined : viewportPageIndexInStore}
          />
        )}
      </div>
      {showChronologyDialog &&
        ReactDOM.createPortal(
          <AddToChronologyDialog
            selectedText={selectedTextForChronology}
            selectedPageIds={selectedPageIds}
            onClose={handleCloseChronologyDialog}
            clearSelection={handleCloseChronologyDialog}
            onSelectedTextForChronology={setSelectedTextForChronology}
          />,
          document.body,
        )}
    </>
  );
};
